/**
 * AI activity (owner/admin): what the AI did for this workspace, why, with which model and tools, from which data.
 * Behind the server-evaluated `ai.activity_ui` flag (default off): off, unknown or failed -> the ordinary not-found screen
 * and no nav entry. Read-only. The API sends ids, codes and counts only, so nothing here can show prompts or message text.
 */
import { useEffect, useState } from 'react';
import { Badge, Button, Card, CardBody, EmptyState, KitProvider, LoadingRegion, SkeletonText, Table, Tabs, type BadgeTone, type TableColumn } from '@qm/ui/kit';
import { Heading, Loading, PageHeader } from '@qm/ui';
import { formatDate, formatNumber } from '@qm/i18n';
import { ACTIVITY_KINDS, AI_ACTIVITY_FLAG, type ActivityEntry, type ActivityKind, type ActivityPage } from '@qm/contracts/ai-activity';
import { useApp } from '../../../lib/app-context';
import { bff, BffError } from '../../../lib/bff';
import { useFlag } from '../../../lib/flags';
import { NotFoundScreen } from '../../../shell/RouteStates';
import { useWorkspaceName } from '../_shared/ListScreen';
import './ai-activity.css';

export function AiActivityPage() {
  const { t } = useApp();
  const state = useFlag(AI_ACTIVITY_FLAG);
  if (state === 'off') return <NotFoundScreen />;
  if (state === 'loading') {
    return (
      <section className="qm-page" aria-labelledby="page-title">
        <Heading level={1} id="page-title">{t('ai-activity.title')}</Heading>
        <Loading label={t('common.loading')} />
      </section>
    );
  }
  return <AiActivityScreen />;
}

const TONE: Record<ActivityEntry['tone'], BadgeTone> = { ok: 'success', warn: 'warning', error: 'danger', neutral: 'neutral' };
type Filter = ActivityKind | 'all';
type Load = { status: 'loading' } | { status: 'ready'; entries: ActivityEntry[]; next: string | null; more: boolean } | { status: 'forbidden' } | { status: 'error' };

const path = (kind: Filter, before?: string | null): `/v1/${string}` => {
  const q = new URLSearchParams({ limit: '50' });
  if (kind !== 'all') q.set('kind', kind);
  if (before) q.set('before', before);
  return `/v1/ai/activity?${q.toString()}`;
};

function AiActivityScreen() {
  const { t, locale } = useApp();
  const workspace = useWorkspaceName();
  const [filter, setFilter] = useState<Filter>('all');
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    setLoad({ status: 'loading' });
    bff<ActivityPage>(path(filter)).then(
      (p) => { if (live) setLoad({ status: 'ready', entries: p.entries, next: p.nextBefore, more: false }); },
      (e: unknown) => { if (live) setLoad(e instanceof BffError && (e.status === 403 || e.status === 401) ? { status: 'forbidden' } : { status: 'error' }); },
    );
    return () => { live = false; };
  }, [filter, tick]);

  const older = () => {
    if (load.status !== 'ready' || !load.next) return;
    const { entries, next } = load;
    setLoad({ ...load, more: true });
    bff<ActivityPage>(path(filter, next)).then(
      (p) => setLoad({ status: 'ready', entries: [...entries, ...p.entries], next: p.nextBefore, more: false }),
      () => setLoad({ status: 'error' }),
    );
  };

  const money = (micros: number | null) => (micros === null ? '—' : formatNumber(locale, micros / 1_000_000, { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }));
  const columns: TableColumn<ActivityEntry>[] = [
    { key: 'at', header: t('ai-activity.col.time'), cell: (e) => <time dateTime={e.at}>{formatDate(locale, new Date(e.at), { dateStyle: 'medium', timeStyle: 'short' })}</time> },
    { key: 'event', header: t('ai-activity.col.event'), cell: (e) => <EventCell e={e} /> },
    { key: 'model', header: t('ai-activity.col.model'), cell: (e) => <span dir="ltr">{e.model ?? '—'}</span> },
    { key: 'cost', header: t('ai-activity.col.cost'), align: 'end', cell: (e) => <span dir="ltr">{money(e.costMicros)}</span> },
    { key: 'by', header: t('ai-activity.col.by'), cell: (e) => (e.actor ? <span dir="ltr" className="qaa__id">{e.actor}</span> : t('ai-activity.system')) },
  ];

  return (
    <KitProvider locale={locale}>
      <section className="qm-page qaa" aria-labelledby="page-title">
        <PageHeader eyebrow={workspace} title={t('ai-activity.title')} intro={t('ai-activity.intro')} actions={<Button variant="secondary" onClick={() => setTick((n) => n + 1)}>{t('ai-activity.refresh')}</Button>} />
        <p className="qaa__muted">{t('ai-activity.privacy')}</p>
        <Tabs label={t('ai-activity.filter.label')} value={filter} onChange={(id) => setFilter(id as Filter)}
          items={[{ id: 'all', label: t('ai-activity.filter.all') }, ...ACTIVITY_KINDS.map((k) => ({ id: k, label: t(`ai-activity.kind.${k}`) }))]} />
        {load.status === 'loading' ? (
          <LoadingRegion label={t('common.loading')}><SkeletonText lines={5} /></LoadingRegion>
        ) : load.status === 'forbidden' ? (
          <EmptyState title={t('ai-activity.forbidden')} />
        ) : load.status === 'error' ? (
          <EmptyState tone="error" title={t('shell.error.load')} actions={<Button onClick={() => setTick((n) => n + 1)}>{t('ai-activity.retry')}</Button>} />
        ) : (
          <Card>
            <CardBody>
              <Table caption={t('ai-activity.table')} columns={columns} rows={load.entries} empty={<p className="qaa__muted">{t('ai-activity.empty')}</p>} stack />
              {load.next ? <div className="qaa__more"><Button variant="secondary" loading={load.more} onClick={older}>{t('ai-activity.loadMore')}</Button></div> : null}
            </CardBody>
          </Card>
        )}
      </section>
    </KitProvider>
  );
}

/** Outcome codes are translated; unknown codes show as they are (never a raw key). */
export function EventCell({ e }: { e: ActivityEntry }) {
  const { t } = useApp();
  const params: Record<string, string | number> = { ...e.params };
  if (typeof params.outcome === 'string') params.outcome = tr(t, `ai-activity.outcome.${params.outcome}`, params.outcome);
  const rows = Object.entries(e.details).filter(([, v]) => v !== null && !(Array.isArray(v) && v.length === 0));
  return (
    <div className="qaa__event">
      <div className="qaa__title"><Badge tone={TONE[e.tone]} dot>{t(`ai-activity.kind.${e.kind}`)}</Badge><span>{t(e.titleKey, params)}</span></div>
      {rows.length ? (
        <details className="qaa__details">
          <summary>{t('ai-activity.details')}</summary>
          <dl>
            {rows.map(([k, v]) => (
              <div key={k} className="qaa__row">
                <dt>{tr(t, `ai-activity.detail.${k}`, k)}</dt>
                <dd dir="ltr">{Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? t(v ? 'ai-activity.detail.yes' : 'ai-activity.detail.no') : String(v)}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
    </div>
  );
}

/** Translate when the key exists; otherwise show the plain code. */
function tr(t: (k: string) => string, key: string, fallback: string): string {
  const v = t(key);
  return v === key ? fallback : v;
}
